export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";

type StateChangeListener<T> = (newValue: T, oldValue: T) => void;

export class UIState {
  private static _instance: UIState | null = null;

  private _viewportWidth = window.innerWidth;
  private _viewportHeight = window.innerHeight;
  private _currentBreakpoint: Breakpoint = "xs";
  private _isHeaderSticked = false;
  private _isSidebarOpen = false;
  private _isNavbarVisible = true;
  private _isAtPageEnd = false;

  private readonly listeners: Map<string, StateChangeListener<any>[]> =
    new Map();

  private constructor() {}

  static getInstance(): UIState {
    UIState._instance ??= new UIState();
    return UIState._instance;
  }

  get viewportWidth(): number {
    return this._viewportWidth;
  }

  set viewportWidth(value: number) {
    const oldValue = this._viewportWidth;
    this._viewportWidth = value;
    this.notifyListeners("viewportWidth", value, oldValue);
  }

  get viewportHeight(): number {
    return this._viewportHeight;
  }

  set viewportHeight(value: number) {
    const oldValue = this._viewportHeight;
    this._viewportHeight = value;
    this.notifyListeners("viewportHeight", value, oldValue);
  }

  get currentBreakpoint(): Breakpoint {
    return this._currentBreakpoint;
  }
  set currentBreakpoint(value: Breakpoint) {
    const oldValue = this._currentBreakpoint;
    this._currentBreakpoint = value;
    this.notifyListeners("currentBreakpoint", value, oldValue);
  }

  get isSidebarOpen(): boolean {
    return this._isSidebarOpen;
  }

  set isSidebarOpen(value: boolean) {
    const oldValue = this._isSidebarOpen;
    this._isSidebarOpen = value;
    this.notifyListeners("isSidebarOpen", value, oldValue);
  }

  get isNavbarVisible(): boolean {
    return this._isNavbarVisible;
  }

  set isNavbarVisible(value: boolean) {
    const oldValue = this._isNavbarVisible;
    this._isNavbarVisible = value;
    this.notifyListeners("isNavbarVisible", value, oldValue);
  }

  get isHeaderSticked(): boolean {
    return this._isHeaderSticked;
  }

  set isHeaderSticked(value: boolean) {
    const oldValue = this._isHeaderSticked;
    this._isHeaderSticked = value;
    this.notifyListeners("isHeaderSticked", value, oldValue);
  }

  get isAtPageEnd(): boolean {
    return this._isAtPageEnd;
  }

  set isAtPageEnd(value: boolean) {
    const oldValue = this._isAtPageEnd;
    this._isAtPageEnd = value;
    this.notifyListeners("isAtPageEnd", value, oldValue);
  }

  subscribe<T>(property: string, callback: StateChangeListener<T>): () => void {
    if (!this.listeners.has(property)) {
      this.listeners.set(property, []);
    }
    this.listeners.get(property)!.push(callback);

    return () => {
      const propertyListeners = this.listeners.get(property);
      if (propertyListeners) {
        const index = propertyListeners.indexOf(callback);
        if (index > -1) {
          propertyListeners.splice(index, 1);
        }
      }
    };
  }

  private notifyListeners<T>(property: string, newValue: T, oldValue: T): void {
    const propertyListeners = this.listeners.get(property);
    if (propertyListeners) {
      for (const callback of propertyListeners) {
        callback(newValue, oldValue);
      }
    }
  }

  get isMobile(): boolean {
    return this._currentBreakpoint === "xs";
  }

  get isTablet(): boolean {
    return this._currentBreakpoint === "sm";
  }

  get isSmallDevice(): boolean {
    return ["xs", "sm", "md", "lg"].includes(this._currentBreakpoint);
  }

  get isDesktop(): boolean {
    return ["sm", "md", "lg", "xl"].includes(this._currentBreakpoint);
  }

  get isDesktopOnly(): boolean {
    return ["md", "lg", "xl"].includes(this._currentBreakpoint);
  }
}
